import { useCallback, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';

type ColumnWidths<T extends string> = Record<T, number>;

type UseColumnWidthsReturn<T extends string> = {
    columnWidths: ColumnWidths<T>;
    createLayoutHandler: (column: T) => (event: LayoutChangeEvent) => void;
};

/**
 * Hook for dynamically sizing columns to fit their widest content.
 * Tracks the maximum width measured for each column and updates minWidth values accordingly.
 *
 * @param defaultWidths - Initial minimum widths for each column
 * @returns Object containing:
 *   - columnWidths: Current minimum widths for each column (use with minWidth prop)
 *   - createLayoutHandler: Factory function to create onLayout handlers for each column
 *
 * @example
 * const { columnWidths, createLayoutHandler } = useColumnWidths({
 *     entry: 52,
 *     payout: 52,
 * });
 *
 * // In render:
 * <Box minWidth={columnWidths.entry}>
 *     <Text onLayout={createLayoutHandler('entry')}>{value}</Text>
 * </Box>
 */
export const useColumnWidths = <T extends string>(defaultWidths: ColumnWidths<T>): UseColumnWidthsReturn<T> => {
    const [columnWidths, setColumnWidths] = useState<ColumnWidths<T>>(defaultWidths);

    const createLayoutHandler = useCallback(
        (column: T) => (event: LayoutChangeEvent) => {
            const measuredWidth = event.nativeEvent.layout.width;
            setColumnWidths(prev => {
                if (measuredWidth > prev[column]) {
                    return { ...prev, [column]: measuredWidth };
                }
                return prev;
            });
        },
        []
    );

    return { columnWidths, createLayoutHandler };
};
